import { TCallback } from '../../../shared/types/utilityTypes';
import frontendLog from '../libs/frontendLog';
import { networkService } from '../../../../../shared/frontend/scripts/services/networkService';
import {
  TBossRankingsServerState,
  TCharacterDetailsServerState,
  TGuildDetailsServerState,
  TGuildRosterServerState,
  TGuildSnapshotDetailsServerState,
  TProgressRankingsServerState,
  TRaceProgressServerState,
  TRaidRankingsServerState,
  TRealmsServerState
} from '../libs/store';
import { buildUrl, checkGetResult } from '../../../../../shared/frontend/libs/utils';
import { commonApiService } from '../../../../../shared/frontend/scripts/services/commonApiService';

export type TGetRaidInstanceRankingsParams = {
  realm?: string;
  raid: string;
  difficulty: string;
  region: string;
  page: number;
  faction?: string;
  guildsFilter?: string;
  limit?: number;
  recent?: boolean;
};

export type TGetGuildRankingsParams = {
  filter: string;
  realm?: string;
  region: string;
  page: number;
  faction?: string;
  limit?: number;
};

export type TGetCharacterRankingsParams = {
  filter: string;
  realm?: string;
  region: string;
  page: number;
  faction?: string;
  limit?: number;
  class_id?: number;
};

export type TGetRaidLeaderboardWidgetDataParams = {
  realm?: string;
  raid: string;
  difficulty: string;
  region: string;
  page: number;
  faction?: string;
  guildsFilter?: string;
  limit?: number;
  recent?: boolean;
};

export type TGetRaidLoggedEncounterLiveParams = {
  raid: string;
  filter: string;
  page: number;
  bosses: string;
  guildIds: string;
};

export type TGetGuildCharacterAchievementsParams = {
  region: string;
  guild?: string;
  realm?: string;
  tier?: string;
};

export type TGetBossRankingsParams = {
  boss: string;
  raid: string;
  difficulty: string;
  region: string;
  page: number;
  faction?: string;
  realm?: string;
};

export type TGetProgressRankingsParams = {
  progress: number;
  raid: string;
  difficulty: string;
  region: string;
  page: number;
  faction?: string;
  realm?: string;
};

export type TGetRaceProgressParams = {
  raid: string;
  difficulty: string;
  region: string;
  realm?: string;
  faction?: string;
  includeLogData?: boolean;
  instanceId?: string;
};

export type TGetTeamRosterParams = {
  region: string;
  team: string;
};

export type TGetGuildDetailsParams = {
  region: string;
  realm: string;
  guild: string;
  expansion?: number;
};

export type TGetGuildSnapshotDetailsParams = {
  mode: string;
  id: number;
  timestamp: string;
};

export type TGetGuildRosterParams = {
  region: string;
  realm: string;
  guild: string;
};

export type TGetRealmsPageParams = {
  region: string;
  realm: string;
};

export type TGetRealmPageParams = {
  region: string;
  realm: string;
};

export type TGetGuildBossRankings = {
  region: string;
  realm: string;
  guild: string;
  raid: string;
  difficulty: string;
};

export type TGetCharacterBossRankings = {
  region: string;
  realm: string;
  character: string;
  raid: string;
  difficulty: string;
};

export type TGetGuildBossKillDetails = {
  region: string;
  realm: string;
  guild: string;
  raid: string;
  difficulty: string;
  boss: string;
};

export type TGetGuildBossAttempts = {
  region: string;
  realm: string;
  guild: string;
  raid: string;
  difficulty: string;
  boss: string;
  limit?: number;
};

export type TGetCharacterDetailsParams = {
  tier: string; // should follow season behavior probably
  loadout?: string;
  region: string;
  realm: string;
  character: string;
};

export type TGetAccountCharactersParams = {
  region: string;
  realm: string;
  character: string;
};

export type TGetCharacterSnapshotDetailsParams = {
  id: number;
  timestamp: string;
  mode: string;
};

export type TGetCharacterHistoryParams = {
  characterId: number;
};

export type TSearchParams = {
  term: string;
  type?: 'all' | 'guild' | 'realm' | 'character';
};

export type TSearchAdvancedParams = {
  term: string;
  type?: 'all' | 'guild' | 'realm' | 'character';
};

export type TCreateThreadPost = {
  threadId: number;
  content: string;
};

export type TApplicationRequestParams = {
  message?: string;
  targetProfileId: number;
  sourceProfileId: number;
};

const apiService = {
  ...commonApiService,

  getQueueBatchInfo(batchId: string, callback: TCallback<any>) {
    const hostPath = '/api/crawler/monitor';
    const url = buildUrl(hostPath, { batchId });
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getRaidRaceTracker
   */
  getRaidRaceTracker(params: TGetRaidInstanceRankingsParams, callback: TCallback<TRaidRankingsServerState>) {
    const hostPath = '/api/raids/raid-race';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getRaidInstanceRankings
   */
  getRaidInstanceRankings(params: TGetRaidInstanceRankingsParams, callback: TCallback<TRaidRankingsServerState>) {
    const hostPath = '/api/raids/instance-rankings';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => checkGetResult(data, callback),
      (err) => callback(err)
    );
  },

  /**
   * getGuildRankings
   */
  getGuildRankings(params: TGetGuildRankingsParams, callback: TCallback<TRaidRankingsServerState>) {
    const hostPath = '/api/guilds/rankings';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => checkGetResult(data, callback),
      (err) => callback(err)
    );
  },

  /**
   * getCharacterRankings
   */
  getCharacterRankings(params: TGetCharacterRankingsParams, callback: TCallback<TRaidRankingsServerState>) {
    const hostPath = '/api/characters/rankings';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => checkGetResult(data, callback),
      (err) => callback(err)
    );
  },

  /**
   * getRaidInstanceRankings
   */
  getRaidLeaderboardWidgetData(
    params: TGetRaidLeaderboardWidgetDataParams,
    callback: TCallback<TRaidRankingsServerState>
  ) {
    const hostPath = '/api/raids/raid-leaderboard-widget';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getRaidLoggedEncounterLive
   */
  getRaidLoggedEncounterLive(params: TGetRaidLoggedEncounterLiveParams, callback: TCallback<any>) {
    const hostPath = '/api/live-tracking/raid-encounters';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getRaidLoggedEncounterDetails
   */
  getRaidLoggedEncounterDetails(params: any, callback: TCallback<any>) {
    const hostPath = `/api/raid/logged-encounters/${params.raid}/${params.id}`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGuildCharacterAchievements
   */
  getGuildCharacterAchievements(params: TGetGuildCharacterAchievementsParams, callback: TCallback<any>) {
    const hostPath = '/api/guilds/roster-achievements';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getBossRankings
   */
  getBossRankings(params: TGetBossRankingsParams, callback: TCallback<TBossRankingsServerState>) {
    const hostPath = '/api/raids/boss-rankings';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getProgressRankings
   */
  getProgressRankings(params: TGetProgressRankingsParams, callback: TCallback<TProgressRankingsServerState>) {
    const hostPath = '/api/raids/progress-rankings';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * addGuild
   */
  addGuild(params: any, callback: TCallback<any>) {
    const hostPath = '/api/crawler/guilds';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addGuild request error');
        callback(err);
      });
  },

  /**
   * login
   */
  login(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/login';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'login request error');
        callback(err);
      });
  },

  /**
   * forgotPassword
   */
  forgotPassword(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/forgot';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'forgot password request error');
        callback(err);
      });
  },

  /**
   * resetPassword
   */
  resetPassword(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/reset';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'resetPassword request error');
        callback(err);
      });
  },

  /**
   * sendAddressVerificationEmail
   */
  sendAddressVerificationEmail(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/login/resend';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'sendAddressVerificationEmail request error');
        callback(err);
      });
  },

  /**
   * register
   */
  register(params: any, callback: TCallback<any>) {
    const hostPath = '/api/register';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'register request error');
        callback(err);
      });
  },

  /**
   * agreeToPolicies
   */
  agreeToPolicies(callback: TCallback<any>) {
    const hostPath = '/api/agree-to-policies';
    const url = buildUrl(hostPath, {});
    networkService.post(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'agreeToPolicies request error');
        callback(err);
      });
  },

  /**
   * addCharacter
   */
  addCharacter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/crawler/characters';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addCharacter request error');
        callback(err);
      });
  },

  /**
   * getRaceProgress
   */
  getRaceProgress(params: TGetRaceProgressParams, callback: TCallback<TRaceProgressServerState>) {
    const hostPath = '/api/hall-of-fame';
    const queryParams = params;

    const url = buildUrl(hostPath, queryParams);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGuildDetails
   */
  getGuildDetails(params: TGetGuildDetailsParams, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/guilds/details';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGuildSnapshotDetails
   */
  getGuildSnapshotDetails(
    params: TGetGuildSnapshotDetailsParams,
    callback: TCallback<TGuildSnapshotDetailsServerState>
  ) {
    const hostPath = `/api/guilds/snapshots/${params.mode}/${params.id}/${params.timestamp}`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGuildRoster
   */
  getGuildRoster(params: TGetGuildRosterParams, callback: TCallback<TGuildRosterServerState>) {
    const hostPath = '/api/guilds/roster';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGuildRaidRankings
   */
  getGuildRaidRankings(params: TGetGuildBossRankings, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/guilds/raid-rankings';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGuildBossKillDetails
   */
  getGuildBossKillDetails(params: TGetGuildBossKillDetails, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/guilds/boss-kills';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGuildBossAttempts
   */
  getGuildBossAttempts(params: TGetGuildBossAttempts, callback: TCallback<any>) {
    const hostPath = '/api/guilds/boss-attempts';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getCharacterDetails
   */
  getCharacterDetails(params: TGetCharacterDetailsParams, callback: TCallback<TCharacterDetailsServerState>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}`;
    const queryParams = {
      tier: params.tier,
      loadout: params.loadout
    };

    const url = buildUrl(hostPath, queryParams);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getAccountCharacters
   */
  getAccountCharacters(params: TGetAccountCharactersParams, callback: TCallback<any>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/alts`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getCharacterSnapshotDetails
   */
  getCharacterSnapshotDetails(
    params: TGetCharacterSnapshotDetailsParams,
    callback: TCallback<TCharacterDetailsServerState>
  ) {
    const hostPath = `/api/characters/snapshots/${params.mode}/${params.id}/${params.timestamp}`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getCharacterHistory
   */
  getCharacterHistory(params: TGetCharacterHistoryParams, callback: TCallback<TCharacterDetailsServerState>) {
    const hostPath = '/api/characters/persona-history';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getCharacterRaidProgress
   */
  getCharacterRaidProgress(params: Omit<TGetCharacterDetailsParams, 'season'>, callback: TCallback<any>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/raid-progress`;
    const queryParams = {
      tier: params.tier
    };

    const url = buildUrl(hostPath, queryParams);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getCharacterRaidRankings
   */
  getCharacterRaidRankings(params: TGetCharacterBossRankings, callback: TCallback<TCharacterDetailsServerState>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/raids/${params.raid}/${params.difficulty}`;
    const queryParams = {};

    const url = buildUrl(hostPath, queryParams);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getConnectedRealms
   */
  getConnectedRealms(params: TGetRealmsPageParams, callback: TCallback<TRealmsServerState>) {
    const url = buildUrl('/api/connected-realms', params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * search
   */
  search(params: TSearchParams, callback: TCallback<any>) {
    const hostPath = '/api/search';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * searchAdvanced
   */
  searchAdvanced(params: TSearchAdvancedParams, callback: TCallback<any>) {
    const hostPath = '/api/search-advanced';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getSidebarContent
   */
  getSidebarContent(slug: string, callback: TCallback<any>) {
    const hostPath = `/api/sidebar/${slug}`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getUserBattleNetSettings
   */
  getUserBattleNetSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/battlenet';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getEventRegistrationData
   */
  getEventRegistrationData(params: any, callback: TCallback<any>) {
    const hostPath = '/api/events/register';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * saveEventRegistrationData
   */
  saveEventRegistrationData(params: any, callback: TCallback<any>) {
    const hostPath = '/api/events/register';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * revokeBattleNetAuth
   *
   * TODO: FIX XSS potential here
   */
  revokeBattleNetAuth(callback: TCallback<any>) {
    const hostPath = '/battlenet/revoke';
    const url = buildUrl(hostPath, {});
    networkService.post(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'revokeBattleNetAuth request error');
        callback(err);
      });
  },

  /**
   * revokePatreonAuth
   *
   * TODO: FIX XSS potential here
   */
  revokePatreonAuth(callback: TCallback<any>) {
    const hostPath = '/patreon/revoke';
    const url = buildUrl(hostPath, {});
    networkService.post(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'revokePatreonAuth request error');
        callback(err);
      });
  },

  /**
   * getUserTimeSettings
   */
  getUserTimeSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/time';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getUserProfileSettings
   */
  getUserProfileSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/profile';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getBanList
   */
  getBanList(params: { userId?: number; groupId?: number }, callback: TCallback<any>) {
    const hostPath = '/api/settings/block-list';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * addBanEntry
   */
  addBanEntry(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/block-list/add';
    const url = buildUrl(hostPath, {});

    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addBanEntry request error');
        callback(err);
      });
  },

  /**
   * deleteBanEntry
   */
  deleteBanEntry(entryId: number, callback: TCallback<any>) {
    const hostPath = '/api/settings/block-list/delete';
    const url = buildUrl(hostPath, { id: entryId });

    networkService.post(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'deleteBanEntry request error');
        callback(err);
      });
  },

  /**
   * getUserNotificationSettings
   */
  getUserNotificationSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/notifications';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getUserPatreonSettings
   */
  getUserPatreonSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/patreon';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getUserVanityAliasSettings
   */
  getUserVanityAliasSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/vanity-alias';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getUserEmailSettings
   */
  getUserEmailSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/email';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * resendEmailChangeVerification
   */
  resendEmailChangeVerification(callback: TCallback<any>) {
    const hostPath = '/api/settings/email/resend';
    const url = buildUrl(hostPath, {});
    networkService.post(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'resendEmailChangeVerification request error');
        callback(err);
      });
  },

  /**
   * saveUserEmailSettings
   */
  saveUserEmailSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/email';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserEmailSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserPasswordSettings
   */
  saveUserPasswordSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/password';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserPasswordSettings request error');
        callback(err);
      });
  },

  /**
   * saveDeleteAccountSettings
   */
  saveDeleteAccountSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/delete-account';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveDeleteAccountSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserUsernameSettings
   */
  saveUserUsernameSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/username';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserUsernameSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserNotificationSettings
   */
  saveUserNotificationSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/notifications';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserNotificationSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserPatreonSettings
   */
  saveUserPatreonSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/patreon';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserPatreonSettings request error');
        callback(err);
      });
  },

  /**
   * addUserVanityAlias
   */
  addUserVanityAlias(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/vanity-alias/add';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addUserVanityAlias request error');
        callback(err);
      });
  },

  /**
   * deleteUserVanityAlias
   */
  deleteUserVanityAlias(id: number, callback: TCallback<any>) {
    const hostPath = '/api/settings/vanity-alias/delete';
    const url = buildUrl(hostPath, {});
    networkService.post(url, { id } as any)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'deleteUserVanityAlias request error');
        callback(err);
      });
  },

  /**
   * saveUserTimeSettings
   */
  saveUserTimeSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/time';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserTimeSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserProfileSettings
   */
  saveUserProfileSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/profile';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserProfileSettings request error');
        callback(err);
      });
  },

  /**
   * getCharacterPrivacySettings
   */
  getCharacterPrivacySettings(params: any, callback: TCallback<any>) {
    const hostPath = `/api/settings/characters/${params.character_id}/privacy`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getCharacterRecruitmentSettings
   */
  getCharacterRecruitmentSettings(characterId: number, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/characters/${characterId}`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  // fetch all recruitment profiles for specific target
  getTargetRecruitmentProfiles(entityType: string, targetId: number, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/target-profiles';
    const url = buildUrl(hostPath, { entity_type: entityType, target_id: targetId });
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  // fetch all recruitment profiles
  getOwnRecruitmentProfiles(callback: TCallback<any>) {
    const hostPath = '/api/recruitment/own-profiles';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  getFeaturedRecruitmentProfiles(queryParams: any, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/featured-profiles';
    const url = buildUrl(hostPath, queryParams);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  getRecentRecruitmentProfiles(queryParams: any, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/recent-profiles';
    const url = buildUrl(hostPath, queryParams);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * fetching recruitment profiles
   */
  getStaticRecruitmenProfileData(profileId: number, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${profileId}/static`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },
  getRecruitmentProfile(profileId: number, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${profileId}`;
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },
  getProfilesForApplication(targetProfileId: number, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/check-requirements';
    const url = buildUrl(hostPath, { targetProfileId });
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  createRecruitmentProfile(params: any, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/profiles';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'createRecruitmentProfile request error');
        callback(err);
      });
  },

  saveCharacterRecruitmentProfile(params: any, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${params.id}`;
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveCharacterRecruitmentProfile request error');
        callback(err);
      });
  },

  setRecruitmentProfilePublished(params: any, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${params.profileId}/publish`;
    const url = buildUrl(hostPath, {});
    networkService.post(url, { publish: params.publish } as any)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'setRecruitmentProfilePublished request error');
        callback(err);
      });
  },

  // delete recruitment profile
  deleteRecruitmentProfile(profileId: number, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${profileId}`;
    const url = buildUrl(hostPath, {});
    networkService.delete(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'deleteRecruitmentProfile request error');
        callback(err);
      });
  },

  /**
   * saveCharacterPrivacySettings
   */
  saveCharacterPrivacySettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/characters/privacy';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveCharacterPrivacySettings request error');
        callback(err);
      });
  },

  /**
   * getGroupLiveTrackingCustomizationSettings
   */
  getGroupLiveTrackingCustomizationSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/live-tracking';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGroupClientUsage
   */
  getGroupClientUsage(params: { group_id: number; token: string }, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/client-usage';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * saveGroupLiveTrackingCustomizationSettings
   */
  saveGroupLiveTrackingCustomizationSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/live-tracking';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveGroupLiveTrackingCustomizationSettings request error');
        callback(err);
      });
  },

  /**
   * getGroupRoleSettings
   */
  getGroupRoleSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/roles';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * addGroupRole
   */
  addGroupRole(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/roles';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addGroupRole request error');
        callback(err);
      });
  },

  /**
   * removeGroupRole
   */
  removeGroupRole(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/roles';
    const url = buildUrl(hostPath, {});
    networkService.delete(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'removeGroupRole request error');
        callback(err);
      });
  },

  /**
   * getArticleContent
   */
  getArticleContent(params: any, callback: TCallback<any>) {
    const hostPath = '/api/news/article';
    const queryParams = { id: params.id };

    const url = buildUrl(hostPath, queryParams);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getArticlesWeeklyRoute
   */
  getArticlesWeeklyRoute(callback: TCallback<any>) {
    const hostPath = '/api/news/weekly-routes';
    const url = buildUrl(hostPath, {});

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getArticlesPlayLikeThePros
   */
  getArticlesPlayLikeThePros(callback: TCallback<any>) {
    const hostPath = '/api/news/play-like-the-pros';
    const url = buildUrl(hostPath, {});

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getNewsListContent
   */
  getNewsListContent(params: any, callback: TCallback<any>) {
    const hostPath = '/api/news';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data: any) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * listThreadPosts
   */
  listThreadPosts(params: any, callback: TCallback<any>) {
    const hostPath = '/api/threads/list';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data: any) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * peekThreadPosts
   */
  peekThreadPosts(params: any, callback: TCallback<any>) {
    const hostPath = '/api/threads/peek';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data: any) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * refreshThreadPosts
   */
  refreshThreadPosts(params: any, callback: TCallback<any>) {
    const hostPath = '/api/threads/refresh';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data: any) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * listThreadPosts
   */
  createThreadPost(params: TCreateThreadPost, callback: TCallback<any>) {
    const hostPath = `/api/threads/${params.threadId}/posts`;
    const url = buildUrl(hostPath, {});
    networkService.post(url, { content: params.content } as any)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'createThreadPost request error');
        callback(err);
      });
  },

  /**
   * listThreadPosts
   */
  closeThread(threadId: number, callback: TCallback<any>) {
    const hostPath = `/api/threads/${threadId}/close`;
    const url = buildUrl(hostPath, {});
    networkService.post(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'closeThread request error');
        callback(err);
      });
  },

  /**
   * blockCommunication
   */
  blockCommunication(threadId: number, reason: string, callback: TCallback<any>) {
    const hostPath = `/api/threads/${threadId}/block`;
    const url = buildUrl(hostPath, {});
    networkService.post(url, { reason } as any)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'blockCommunication request error');
        callback(err);
      });
  },

  /**
   * getEmbeddedPlayerStream
   */
  getEmbeddedPlayerStream(params: any, callback: TCallback<any>) {
    const hostPath = '/api/streamers/active';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  getFeaturedStreamers(params: any, callback: TCallback<any>) {
    const hostPath = '/api/streamers/featured';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getGeneratedTeamNames
   */
  getGeneratedTeamNames(params: any, callback: TCallback<TBossRankingsServerState>) {
    const hostPath = '/api/teams/name-generator';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * mergeCharacterInstances
   */
  mergeCharacterInstances(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/characters/transfer';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'mergeCharacterInstances request error');
        callback(err);
      });
  },

  /**
   * viewUserProfile
   */
  viewUserProfile(params: any, callback: TCallback<any>) {
    const hostPath = '/api/user/view-profile';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * viewUserCharacters
   */
  viewUserCharacters(params: any, callback: TCallback<any>) {
    const hostPath = '/api/user/view-characters';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getRandomIcon
   */
  getRandomIcon(callback: TCallback<any>) {
    const hostPath = '/api/random-icon';
    const url = buildUrl(hostPath, {});
    networkService.get(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'getRandomIcon request error');
        callback(err);
      });
  },

  listMyMessage(callback: TCallback<any>) {
    const hostPath = '/api/messages/list';
    const url = buildUrl(hostPath, {});
    networkService.get(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'listMyMessage request error');
        callback(err);
      });
  },

  fetchUnreadAmount(callback: TCallback<any>) {
    const hostPath = '/api/messages/unread';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  requestApplication(params: TApplicationRequestParams, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/application/create';
    const url = buildUrl(hostPath, params);
    networkService.post(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'requestApplication request error');
        callback(err);
      });
  },

  viewLiveTrackingBossProgress(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/bossprogress';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  viewLiveTrackingRaidProgress(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/raidprogress';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  viewLiveTrackingGuildLoot(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/guildloot';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  viewLiveTrackingRaidPulls(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/raidpulls';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  viewLiveTrackingRaidComps(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/raidcomps';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  viewLiveTrackingGearSummary(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/gearsummary';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  getLastRwfEvent(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/rwf-event/last';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  getRwfEventFeed(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/rwf-event/feed';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * isValidRWFAnalyzeGuilds
   */
  isValidRWFAnalyzeGuilds(guildsString: string, callback: TCallback<any>) {
    const hostPath = '/api/rwf-analyze/is-valid/guilds';
    const url = buildUrl(hostPath, { guildsString });
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  addRaidEncounterVideo(params: any, callback: TCallback<any>) {
    const hostPath = '/api/guilds/boss-kills/videos';

    networkService.post(hostPath, params).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  removeRaidEncounterVideo(videoId: string, callback: TCallback<any>) {
    const hostPath = `/api/guilds/boss-kills/videos-remove/${videoId}`;
    networkService.post(hostPath).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  viewLiveTrackingBossPulls(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/bosspulls';
    const url = buildUrl(hostPath, params);

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  }
};

const promisify =
  (fn: any) =>
  (...args: any[]) =>
    new Promise((resolve: any, reject: any) =>
      fn(...args, (err: any, results: any) => {
        if (err) {
          reject(err);
        } else {
          resolve(results);
        }
      })
    );

type TApiServiceKeys = keyof typeof apiService;

type TApiServiceAsync = {
  [key in TApiServiceKeys]: (...args: any) => Promise<any>;
};

export const apiServiceAsync: TApiServiceAsync = Object.keys(apiService).reduce((accum: any, key: string) => {
  accum[key] = promisify((apiService as any)[key]);
  return accum;
}, {});

export default apiService;
